import { Link } from "@tanstack/react-router";
import { PageShell } from "@project/user/components/page-shell";
import { Reveal, CountUp } from "@project/user/components/reveal";
import { AboutInitiativesCarousel } from "@project/user/components/about-initiatives-carousel";
import { Button } from "@project/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@project/components/ui/dialog";
import { useI18n } from "@project/lib/use-i18n";
import {
  Heart,
  Users,
  Building2,
  Sparkles,
  Quote,
  BadgeCheck,
  ShieldCheck,
  Flower2,
  HandHeart,
  Calendar,
  BookOpenText,
} from "lucide-react";
import careImg from "@project/assets/home-care.webp";
import medicalImg from "@project/assets/home-medical.webp";
import comfortImg from "@project/assets/image (1).jfif";
import { GALLERY_BY_CATEGORY } from "@project/lib/gallery-images";

const TRIPTYCH_IMAGES = {
  comfort: comfortImg,
  health: medicalImg,
  spirituality: GALLERY_BY_CATEGORY.derasar[0]?.src ?? careImg,
} as const;

const STATS_FOUNDED_YEAR = 2002;

export default function AboutPage() {
  const { t } = useI18n();
  return (
    <PageShell>
      {/* ============== MAGAZINE HERO ============== */}
      <section className="relative overflow-hidden bg-background">
        <div className="bg-primary/10 pointer-events-none absolute -right-40 -top-40 h-[28rem] w-[28rem] rounded-full blur-3xl" />
        <div className="bg-gold/15 pointer-events-none absolute -bottom-40 -left-40 h-[28rem] w-[28rem] rounded-full blur-3xl" />

        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 pb-16 pt-14 md:grid-cols-12 md:py-24">
          {/* Editorial label + headline */}
          <div className="md:col-span-7">
            <Reveal>
              <div className="border-primary/20 bg-primary/5 inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-medium uppercase tracking-[0.22em] text-primary">
                <Sparkles className="h-3.5 w-3.5 text-gold" />{" "}
                {t("pages.about.hero.badge")}
              </div>
            </Reveal>
            <Reveal delay={120}>
              <h1 className="mt-6 font-serif italic text-foreground">
                {t("pages.about.hero.titleStart")}
                <span className="not-italic text-primary">
                  {t("pages.about.hero.titleHighlight")}
                </span>
                {t("pages.about.hero.titleEnd")}
              </h1>
            </Reveal>
            <Reveal delay={220}>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
                {t("pages.about.hero.description")}
              </p>
            </Reveal>
            <Reveal delay={300}>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button
                  asChild
                  size="lg"
                  className="hover:bg-primary/90 rounded-full bg-primary text-primary-foreground"
                >
                  <Link to="/donate">
                    <HandHeart className="mr-2 h-4 w-4" />{" "}
                    {t("pages.about.hero.supportCause")}
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-primary/30 rounded-full bg-background text-primary hover:bg-primary hover:text-primary-foreground"
                >
                  <Link to="/contact">
                    <Calendar className="mr-2 h-4 w-4" />{" "}
                    {t("pages.about.hero.visitHome")}
                  </Link>
                </Button>
              </div>
            </Reveal>
          </div>

          {/* Pull-quote card (Poem Preview & Full Poem Dialog) */}
          <Reveal variant="zoom" delay={250} className="md:col-span-5">
            <figure className="shadow-foreground/5 relative flex h-full min-h-[380px] flex-col justify-between rounded-3xl border border-border bg-card p-7 shadow-xl sm:p-9">
              <div className="absolute -top-5 left-7 grid h-10 w-10 place-items-center rounded-full bg-gold text-gold-foreground shadow">
                <Quote className="h-5 w-5" />
              </div>

              <div className="mt-4 flex-1">
                {/* Intro Line */}
                <p className="mb-4 whitespace-pre-line !text-[1.2rem] italic leading-snug text-primary">
                  {t("pages.about.hero.poem.intro")}
                </p>

                {/* 3 Line Preview of Poem */}
                <div className="border-gold/40 space-y-3 border-l-2 py-1 pl-4">
                  {(() => {
                    const rawLines = t("pages.about.hero.poem.lines", {
                      returnObjects: true,
                    });
                    const lines = Array.isArray(rawLines)
                      ? (rawLines as string[])
                      : [];
                    return lines.slice(0, 3).map(line => (
                      <p
                        key={line}
                        className="!text-[1rem] italic !leading-[1.3] leading-relaxed text-foreground"
                      >
                        {line}
                      </p>
                    ));
                  })()}
                  <div className="mt-1 flex items-center gap-2 font-serif !text-[1.2rem] italic text-muted-foreground">
                    <span>...</span>
                    {/* Read More Dialog */}
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button
                          variant="link"
                          className="mt-2 flex h-auto cursor-pointer items-center gap-1 p-0 font-sans text-xs font-semibold not-italic text-primary underline transition-colors hover:text-gold"
                        >
                          {t("pages.about.hero.poem.readMore")}
                          {/* <ArrowRight className="h-3 w-3" /> */}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col overflow-hidden border-border bg-card p-0">
                        <DialogHeader className="bg-muted/30 border-b border-border p-6 pb-4">
                          <DialogTitle className="flex items-center gap-2 font-serif text-2xl text-primary">
                            <Quote className="h-5 w-5 shrink-0 text-gold" />
                            {t("pages.about.hero.poem.title")}
                          </DialogTitle>
                        </DialogHeader>

                        <div className="flex-1 space-y-6 overflow-y-auto p-6 md:p-8">
                          <p className="border-border/60 whitespace-pre-line border-b pb-4 !text-[1.1rem] text-lg italic !leading-[1.5] text-primary">
                            {t("pages.about.hero.poem.intro")}
                          </p>

                          <div className="border-gold/50 space-y-3 border-l-2 pl-4 text-base leading-relaxed text-foreground md:text-lg">
                            {(() => {
                              const rawLines = t(
                                "pages.about.hero.poem.lines",
                                {
                                  returnObjects: true,
                                }
                              );
                              const lines = Array.isArray(rawLines)
                                ? (rawLines as string[])
                                : [];
                              return lines.map(line => (
                                <p
                                  key={line}
                                  className="py-0.5 !text-[1rem] !leading-[1.3] transition-colors hover:text-primary"
                                >
                                  {line}
                                </p>
                              ));
                            })()}
                          </div>

                          <div className="bg-muted/20 mt-8 flex flex-col gap-4 rounded-xl border-t border-border p-4 pt-6 text-sm sm:flex-row sm:items-center sm:justify-between">
                            <div>
                              <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                {t("pages.about.hero.poem.authorLabel")}
                              </span>
                              <span className="mt-0.5 block font-serif text-base font-bold text-foreground">
                                {t("pages.about.hero.poem.authorName")}
                              </span>
                            </div>
                            <div>
                              <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                Location / Foundation
                              </span>
                              <span className="mt-0.5 block font-serif text-base text-foreground">
                                {t("pages.about.hero.poem.foundation")}
                              </span>
                            </div>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </div>

              <figcaption className="mt-6 border-t border-border pt-4">
                <div className="text-sm font-semibold text-foreground">
                  {t("pages.about.hero.poem.authorName")}
                </div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                  {/* {t("pages.about.hero.poem.authorLabel")} */}
                  {t("pages.about.hero.poem.foundation")}
                </div>
              </figcaption>

              <div className="bg-primary/10 pointer-events-none absolute -bottom-2 -right-2 grid h-12 w-12 place-items-center rounded-2xl text-primary">
                <BadgeCheck className="h-6 w-6" />
              </div>
            </figure>
          </Reveal>
        </div>
      </section>

      {/* ============== IMPACT NUMBERS ============== */}
      <section className="border-border/60 bg-card/40 border-y">
        <div className="mx-auto grid max-w-7xl gap-4 px-4 py-12 sm:grid-cols-2 lg:grid-cols-4">
          {[
            {
              icon: Building2,
              n: 360,
              suffix: "+",
              labelKey: "pages.about.stats.residents" as const,
            },
            {
              icon: Users,
              n: 1000,
              suffix: "+",
              labelKey: "pages.about.stats.donors" as const,
            },
            {
              icon: Heart,
              n: Math.max(1, new Date().getFullYear() - STATS_FOUNDED_YEAR),
              suffix: " Yrs",
              labelKey: "pages.about.stats.years" as const,
            },
            {
              icon: ShieldCheck,
              n: 100,
              suffix: "%",
              labelKey: "pages.about.stats.tax" as const,
            },
          ].map((s, i) => (
            <Reveal key={s.labelKey} delay={i * 90}>
              <div className="flex items-center gap-3 rounded-2xl border border-border bg-white p-5">
                <div className="bg-primary/10 grid h-10 w-10 shrink-0 place-items-center rounded-xl text-primary">
                  <s.icon className="h-4 w-4" />
                </div>
                <div>
                  <div className="font-serif text-2xl font-semibold text-foreground">
                    <CountUp to={s.n} suffix={s.suffix} />
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t(s.labelKey)}
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <AboutInitiativesCarousel />

      {/* ============== OUR STORY (magazine columns) ============== */}
      <section className="mx-auto max-w-7xl px-4 py-20">
        <div className="grid gap-12 md:grid-cols-12">
          <Reveal className="md:col-span-4">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-gold">
              {t("pages.about.story.eyebrow")}
            </p>
            <h2 className="mt-3 font-serif text-foreground">
              {t("pages.about.story.heading")}
            </h2>
            <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-primary">
              <BookOpenText className="h-3.5 w-3.5" />{" "}
              {t("pages.about.story.chronicled")}
            </div>
          </Reveal>

          <div className="space-y-6 text-base leading-relaxed text-muted-foreground md:col-span-8 md:columns-2 md:gap-8 md:[column-rule:1px_solid_var(--color-border)]">
            <p className="break-inside-avoid">
              <span className="float-left mr-3 font-serif text-6xl leading-[0.85] text-primary">
                {t("pages.about.story.dropcap")}
              </span>
              {t("pages.about.story.p1a")}
              <strong className="text-foreground">
                {t("pages.about.story.p1bold")}
              </strong>
              {t("pages.about.story.p1b")}
            </p>
            <p className="break-inside-avoid">{t("pages.about.story.p2")}</p>
            <p className="break-inside-avoid">{t("pages.about.story.p3")}</p>
            <p className="break-inside-avoid">{t("pages.about.story.p4")}</p>
          </div>
        </div>
      </section>

      {/* ============== MILESTONES TIMELINE ============== */}
      <section className="bg-secondary/40">
        <div className="mx-auto max-w-7xl px-4 py-20">
          <Reveal className="text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-gold">
              {t("pages.about.milestones.eyebrow")}
            </p>
            <h2 className="mt-3 font-serif text-foreground">
              {t("pages.about.milestones.heading")}
            </h2>
          </Reveal>

          <ol className="relative mx-auto mt-14 max-w-5xl">
            <div
              aria-hidden
              className="absolute bottom-0 left-1/2 top-0 hidden w-0 -translate-x-1/2 border-l-2 border-dashed border-gold md:block"
            />
            {[
              {
                year: "2002",
                titleKey: "pages.about.milestones.m2002.title",
                bodyKey: "pages.about.milestones.m2002.body",
              },
              {
                year: "2012",
                titleKey: "pages.about.milestones.m2012.title",
                bodyKey: "pages.about.milestones.m2012.body",
              },
              {
                year: "2015",
                titleKey: "pages.about.milestones.m2015.title",
                bodyKey: "pages.about.milestones.m2015.body",
              },
              {
                year: "2022",
                titleKey: "pages.about.milestones.m2022.title",
                bodyKey: "pages.about.milestones.m2022.body",
              },
            ].map((m, i) => {
              const isLeft = i % 2 === 0;
              return (
                <Reveal key={m.year} delay={i * 80}>
                  <li className="relative mb-4 last:mb-0 md:mb-5">
                    <span className="absolute left-1/2 top-8 z-10 hidden h-9 w-9 -translate-x-1/2 place-items-center rounded-full border-2 border-gold bg-primary text-[10px] font-bold text-primary-foreground md:grid">
                      {i + 1}
                    </span>
                    <div className="grid md:grid-cols-2 md:gap-12">
                      <div
                        className={
                          isLeft
                            ? "md:col-start-1 md:pr-12"
                            : "md:col-start-2 md:pl-12"
                        }
                      >
                        <div className="card-lift rounded-2xl border !border-[#efa93194] bg-card p-5 shadow-md">
                          <div className="flex items-baseline gap-3">
                            <span className="font-serif text-2xl text-primary">
                              {m.year}
                            </span>
                            <span className="h-px flex-1 bg-border" />
                          </div>
                          <h3 className="mt-2 font-serif text-foreground">
                            {t(m.titleKey)}
                          </h3>
                          <p className="mt-1 text-sm text-muted-foreground">
                            {t(m.bodyKey)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </li>
                </Reveal>
              );
            })}
          </ol>
        </div>
      </section>

      {/* ============== MISSION · VISION · VALUES ============== */}
      <section className="mx-auto max-w-7xl px-4 py-20">
        <div className="grid gap-6 md:grid-cols-3">
          {[
            {
              icon: Heart,
              tagKey: "pages.about.mvv.mission.tag",
              titleKey: "pages.about.mvv.mission.title",
              bodyKey: "pages.about.mvv.mission.body",
            },
            {
              icon: Flower2,
              tagKey: "pages.about.mvv.vision.tag",
              titleKey: "pages.about.mvv.vision.title",
              bodyKey: "pages.about.mvv.vision.body",
            },
            {
              icon: ShieldCheck,
              tagKey: "pages.about.mvv.values.tag",
              titleKey: "pages.about.mvv.values.title",
              bodyKey: "pages.about.mvv.values.body",
            },
          ].map((c, i) => (
            <Reveal key={c.tagKey} variant="zoom" delay={i * 100}>
              <article className="card-lift group flex h-full flex-col rounded-3xl border border-border bg-card p-7 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="shadow-primary/20 grid h-11 w-11 place-items-center rounded-xl bg-primary text-primary-foreground shadow-md">
                    <c.icon className="h-5 w-5" />
                  </div>
                  <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">
                    {t(c.tagKey)}
                  </span>
                </div>
                <h3 className="mt-5 font-serif text-foreground">
                  {t(c.titleKey)}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {t(c.bodyKey)}
                </p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============== EDITORIAL TRIPTYCH ============== */}
      <section className="bg-card/40 border-border/60 -mb-20 border-y">
        <div className="mx-auto max-w-7xl px-4 py-20">
          <Reveal className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-gold">
              {t("pages.about.triptych.eyebrow")}
            </p>
            <h2 className="mt-3 font-serif text-foreground">
              {t("pages.about.triptych.heading")}
            </h2>
            <p className="mt-3 text-muted-foreground">
              {t("pages.about.triptych.description")}
            </p>
          </Reveal>

          <div className="mt-12 grid gap-4 md:grid-cols-12">
            <Reveal variant="zoom" className="md:col-span-7">
              <figure className="card-lift group relative overflow-hidden rounded-3xl border border-border shadow-sm">
                <img
                  src={TRIPTYCH_IMAGES.comfort}
                  alt={t("pages.about.triptych.careAlt")}
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-transparent" />
                <figcaption className="absolute inset-x-0 bottom-0 p-6 text-primary-foreground">
                  <div className="text-gold/90 text-xs uppercase tracking-[0.22em]">
                    {t("pages.about.triptych.morningsLabel")}
                  </div>
                  <div className="mt-1 font-serif text-2xl">
                    {t("pages.about.triptych.morningsText")}
                  </div>
                </figcaption>
              </figure>
            </Reveal>

            <div className="grid gap-4 md:col-span-5">
              <Reveal variant="zoom" delay={120}>
                <figure className="card-lift group relative overflow-hidden rounded-3xl border border-border shadow-sm">
                  <img
                    src={TRIPTYCH_IMAGES.health}
                    alt={t("pages.about.triptych.medicalAlt")}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-transparent" />
                  <figcaption className="absolute inset-x-0 bottom-0 p-5 text-primary-foreground">
                    <div className="text-gold/90 text-[10px] uppercase tracking-[0.22em]">
                      {t("pages.about.triptych.afternoonsLabel")}
                    </div>
                    <div className="mt-1 font-serif text-lg">
                      {t("pages.about.triptych.afternoonsText")}
                    </div>
                  </figcaption>
                </figure>
              </Reveal>
              <Reveal variant="zoom" delay={220}>
                <figure className="card-lift group relative overflow-hidden rounded-3xl border border-border shadow-sm">
                  <img
                    src={TRIPTYCH_IMAGES.spirituality}
                    alt={t("pages.about.triptych.mealAlt")}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-transparent" />
                  <figcaption className="absolute inset-x-0 bottom-0 p-5 text-primary-foreground">
                    <div className="text-gold/90 text-[10px] uppercase tracking-[0.22em]">
                      {t("pages.about.triptych.eveningsLabel")}
                    </div>
                    <div className="mt-1 font-serif text-lg">
                      {t("pages.about.triptych.eveningsText")}
                    </div>
                  </figcaption>
                </figure>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
